import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { useCreateTenant } from '../hooks/useCreateTenant'
import { useCreateOrganization } from '@/features/organizations/hooks/useCreateOrganization'

const createTenantSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be 50 characters or less')
    .regex(/^[a-z0-9_-]+$/, 'Name can only contain lowercase letters, numbers, hyphens (-), and underscores (_)')
    .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
      message: 'Name cannot start or end with a hyphen',
    }),
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be 100 characters or less')
    .refine((val) => val.trim().length > 0, {
      message: 'Display name cannot be only whitespace',
    }),
  isActive: z.boolean(),
  createOrg: z.boolean(),
  orgDomain: z
    .string()
    .max(253, 'Domain must be 253 characters or less')
    .optional()
    .refine((val) => {
      if (!val || val === '') return true
      const domainRegex = /^(?!-)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
      return domainRegex.test(val)
    }, {
      message: 'Invalid domain format (e.g., example.com)',
    }),
}).refine((data) => {
  // If createOrg is checked, orgDomain is required
  if (data.createOrg && (!data.orgDomain || data.orgDomain.trim() === '')) {
    return false
  }
  return true
}, {
  message: 'Domain is required when creating an organization',
  path: ['orgDomain'],
})

type CreateTenantForm = z.infer<typeof createTenantSchema>

interface CreateTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateTenantDialog({ open, onOpenChange }: CreateTenantDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const createTenant = useCreateTenant()
  const { mutateAsync: createOrganization } = useCreateOrganization()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<CreateTenantForm>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: {
      name: '',
      displayName: '',
      isActive: true,
      createOrg: false,
      orgDomain: '',
    },
  })

  const isActive = watch('isActive')
  const createOrg = watch('createOrg')

  const onSubmit = async (data: CreateTenantForm) => {
    setIsSubmitting(true)
    try {
      // Create tenant first
      const response = await createTenant.mutateAsync({
        name: data.name,
        displayName: data.displayName,
        isActive: data.isActive,
      })

      // Extract tenant from response
      const tenant = response?.data?.data

      // If createOrg is checked, create organization with same name
      if (data.createOrg && data.orgDomain) {
        await createOrganization({
          name: data.name,
          displayName: data.displayName,
          domain: data.orgDomain,
          isActive: data.isActive,
          tenantId: tenant._id,
        })
      }

      reset()
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription>
            Create a new tenant to organize and isolate resources.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="my-tenant"
              {...register('name')}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Unique identifier (lowercase letters, numbers, hyphens, underscores)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              placeholder="My Tenant"
              {...register('displayName')}
              disabled={isSubmitting}
            />
            {errors.displayName && (
              <p className="text-sm text-destructive">{errors.displayName.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Human-readable name for the tenant
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="isActive">Active</Label>
              <p className="text-xs text-muted-foreground">
                Enable or disable this tenant
              </p>
            </div>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="createOrg"
                checked={createOrg}
                onCheckedChange={(checked) => setValue('createOrg', checked === true)}
                disabled={isSubmitting}
              />
              <Label htmlFor="createOrg" className="text-sm font-medium">
                Create organization with same name
              </Label>
            </div>

            {createOrg && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="orgDomain">Organization Domain *</Label>
                <Input
                  id="orgDomain"
                  placeholder="e.g., example.com"
                  {...register('orgDomain')}
                  disabled={isSubmitting}
                />
                {errors.orgDomain && (
                  <p className="text-sm text-destructive">{errors.orgDomain.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Domain for the organization (must be unique)
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : createOrg ? 'Create Tenant & Org' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
